import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
// Tabs and Select components removed - signup functionality disabled
import { Building2, Eye, EyeOff, Mail, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const { login, isAuthenticated } = useAuth();
  const [loginFormData, setLoginFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showOtpLogin, setShowOtpLogin] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpFormData, setOtpFormData] = useState({
    email: '',
    otp: ''
  });

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(loginFormData.email, loginFormData.password);
    
    if (result.success) {
      toast.success('Login successful!');
    } else {
      setError(result.error);
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  const handleLoginChange = (e) => {
    setLoginFormData({
      ...loginFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleOtpChange = (e) => {
    setOtpFormData({
      ...otpFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleSendOtp = (e) => {
    e.preventDefault();
    // Display purpose only - no backend connection
    toast.info('OTP functionality will be available soon!');
    setOtpSent(true);
  };

  const handleOtpLogin = (e) => {
    e.preventDefault();
    // Display purpose only - no backend connection
    toast.info('OTP login functionality will be available soon!');
  };

  const handleGoogleLogin = () => {
    // Display purpose only - no backend connection
    toast.info('Google OAuth will be available soon!');
  };

  const handleMicrosoftLogin = () => {
    // Display purpose only - no backend connection
    toast.info('Microsoft OAuth will be available soon!');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4 overflow-y-auto">
      <div className="w-full max-w-md my-4">
        {/* Company Branding */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-3">
            <div className="flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-blue-600 rounded-xl shadow-lg">
              <Building2 className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </div>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Anomatrix</h2>
          <p className="mt-1 text-sm sm:text-base text-gray-600">Financial Anomaly Detection Platform</p>
        </div>

        {/* Login/Signup Form */}
        <Card className="shadow-xl border-0">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl sm:text-2xl text-center">Welcome Back</CardTitle>
            <CardDescription className="text-center text-xs sm:text-sm">
              Sign in to your account
            </CardDescription>
          </CardHeader>
          <CardContent className="pb-6">
            {error && (
              <Alert className="mb-3 border-red-200 bg-red-50">
                <AlertDescription className="text-red-600 text-sm">
                  {error}
                </AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-3">
              {/* OAuth Buttons */}
              <div className="space-y-2">
                <Button 
                  type="button"
                  variant="outline"
                  className="w-full h-10 sm:h-11 text-sm sm:text-base font-medium border-2 hover:bg-gray-50"
                  onClick={handleGoogleLogin}
                  data-testid="google-oauth-button"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>

                <Button 
                  type="button"
                  variant="outline"
                  className="w-full h-10 sm:h-11 text-sm sm:text-base font-medium border-2 hover:bg-gray-50"
                  onClick={handleMicrosoftLogin}
                  data-testid="microsoft-oauth-button"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" viewBox="0 0 23 23">
                    <path fill="#f35325" d="M0 0h11v11H0z"/>
                    <path fill="#81bc06" d="M12 0h11v11H12z"/>
                    <path fill="#05a6f0" d="M0 12h11v11H0z"/>
                    <path fill="#ffba08" d="M12 12h11v11H12z"/>
                  </svg>
                  Continue with Microsoft
                </Button>
              </div>

              {/* Divider */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">Or</span>
                </div>
              </div>

              {/* Login Method Toggle */}
              <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
                <Button
                  type="button"
                  variant={!showOtpLogin ? "default" : "ghost"}
                  className="flex-1 h-9 text-xs sm:text-sm"
                  onClick={() => {
                    setShowOtpLogin(false);
                    setOtpSent(false);
                    setError('');
                  }}
                >
                  <Mail className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  Password
                </Button>
                <Button
                  type="button"
                  variant={showOtpLogin ? "default" : "ghost"}
                  className="flex-1 h-9 text-xs sm:text-sm"
                  onClick={() => {
                    setShowOtpLogin(true);
                    setError('');
                  }}
                >
                  <Smartphone className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  OTP
                </Button>
              </div>

              {/* Email & Password Login Form */}
              {!showOtpLogin ? (
                <form onSubmit={handleLoginSubmit} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="login-email" className="text-sm">Email</Label>
                    <Input
                      id="login-email"
                      name="email"
                      type="email"
                      required
                      placeholder="admin@propelligence.com"
                      value={loginFormData.email}
                      onChange={handleLoginChange}
                      className="h-10"
                      data-testid="login-email-input"
                    />
                  </div>
                  
                  <div className="space-y-1.5">
                    <Label htmlFor="login-password" className="text-sm">Password</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        placeholder="Enter your password"
                        value={loginFormData.password}
                        onChange={handleLoginChange}
                        className="h-10 pr-10"
                        data-testid="login-password-input"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </Button>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-10 sm:h-11 text-sm sm:text-base font-semibold" 
                    disabled={loading}
                    data-testid="login-button"
                  >
                    {loading ? 'Signing In...' : 'Sign In'}
                  </Button>
                </form>
              ) : (
                /* OTP Login Form */
                <form onSubmit={otpSent ? handleOtpLogin : handleSendOtp} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="otp-email" className="text-sm">Email Address</Label>
                    <Input
                      id="otp-email"
                      name="email"
                      type="email"
                      required
                      placeholder="Enter your email"
                      value={otpFormData.email}
                      onChange={handleOtpChange}
                      className="h-10"
                      disabled={otpSent}
                      data-testid="otp-email-input"
                    />
                  </div>

                  {otpSent && (
                    <div className="space-y-1.5">
                      <Label htmlFor="otp-code" className="text-sm">Enter OTP</Label>
                      <Input
                        id="otp-code"
                        name="otp"
                        type="text"
                        required
                        placeholder="6-digit OTP"
                        value={otpFormData.otp}
                        onChange={handleOtpChange}
                        className="h-10 text-center text-lg tracking-widest"
                        maxLength={6}
                        data-testid="otp-code-input"
                      />
                      <p className="text-xs text-gray-500 text-center">
                        OTP sent to {otpFormData.email}
                      </p>
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full h-10 sm:h-11 text-sm sm:text-base font-semibold" 
                    disabled={loading}
                    data-testid={otpSent ? "verify-otp-button" : "send-otp-button"}
                  >
                    {otpSent ? 'Verify OTP' : 'Send OTP'}
                  </Button>

                  {otpSent && (
                    <Button
                      type="button"
                      variant="link"
                      className="w-full text-sm"
                      onClick={() => {
                        setOtpSent(false);
                        toast.info('Resend OTP functionality will be available soon!');
                      }}
                    >
                      Resend OTP
                    </Button>
                  )}
                </form>
              )}

              {/* Demo Credentials - only show for email/password login */}
              {!showOtpLogin && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
                  <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-1.5">Demo Credentials:</h4>
                  <div className="text-xs sm:text-sm text-gray-600 space-y-0.5">
                    <p><span className="font-medium">Admin:</span> admin@propelligence.com / admin123</p>
                    <p className="text-xs text-gray-500">Use these credentials to explore</p>
                  </div>
                </div>
              )}

              {/* Info Notice */}
              <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="text-xs sm:text-sm font-medium text-blue-700 mb-1">
                  {showOtpLogin ? 'OTP Login (Coming Soon)' : 'Need an Account?'}
                </h4>
                <p className="text-xs sm:text-sm text-blue-600">
                  {showOtpLogin 
                    ? 'OTP authentication coming soon. Use email & password for now.'
                    : 'Contact your administrator to create an account.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs sm:text-sm text-gray-500 mt-4">
          Protected by enterprise-grade security
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
